"use client";

import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { sendConsentUpdate } from "@/lib/analytics/consent-mode";
import { track } from "@/lib/analytics/data-layer";
import { PANEL_LAYER_CLASS } from "@/lib/host-panel/layer-stack";
import { useCookieConsent } from "@/lib/legal/cookie-consent";
import { cn } from "@/lib/utils";

/**
 * How much room the notice is taking, published on the root for the layout to reserve.
 *
 * The notice is `position: fixed`, so it occupies no space in the flow and sat **on top of**
 * whatever was at the foot of the page. Measured on the live screen at phone widths: it covered
 * the onboarding's own "Continuar" at 360, 393 and 430px wide, and at those heights there was no
 * scroll left to move the button out from under it — so the person could not submit the form
 * until they had answered a notice we had just decided was not a gate (2026-10-14).
 *
 * Measured rather than declared, because the height is not a constant: it is 224px at 320px wide,
 * 202px from 460px, 86px at 768px, and it changes again with the text's own wrapping and with the
 * person's font size.
 */
export const COOKIE_NOTICE_HEIGHT_VARIABLE = "--tb-cookie-notice-height";

/**
 * How long the notice takes to descend out of the screen, mirroring `--tb-duration-2` (220ms).
 *
 * The number lives here **and** as a token in `globals.css` for the toast's reason
 * (`TOAST_EXIT_MS`): the stylesheet animates, and TypeScript has to know when the animation is
 * over. Here what waits on it is not navigation but the room the bar reserved — releasing
 * {@link COOKIE_NOTICE_HEIGHT_VARIABLE} while the bar is still on screen would pull the page's
 * foot up under a bar that has not left yet, which is the jump the variable exists to prevent.
 * `app/__tests__/design-tokens.test.ts` is where that pair is kept honest.
 */
export const COOKIE_NOTICE_EXIT_MS = 220;

/**
 * The cookie notice: a yes or a no, in a bar at the foot of the window.
 *
 * Not a `Dialog` or `Drawer`: it neither traps focus nor blocks the page, so it is a labelled
 * region. The explanation is the Privacy Policy's section 06; the bar summarises and links.
 *
 * **Layer.** It joins the one scale (`layer-stack.ts`) as `cookieNotice`, at 210.
 *
 * This reverses the choice written here first, which kept the bar out of that scale — it is the
 * Host Panel's, and this bar is the whole site's — and gave it a literal 335, reasoning that it
 * should sit above the panel's floating controls. That reasoning was wrong in one specific way:
 * the mode control (320) and the bulk bar (329) live at **this same bottom edge**, so 335 did not
 * stack over them, it covered them. Until the notice was answered the person could not switch
 * between "Minha lista", "Adicionar peças" and "Ver como convidado" — Playwright measured 54
 * refused clicks, this notice intercepting every one (2026-10-14).
 *
 * What stayed from that reasoning is the part that held: the notice must never cover the toast.
 * At 210 it covers ordinary content and nothing that floats — no drawer, sheet, control, bar,
 * dialog or toast — because a notice about cookies is a yes/no a person may leave for later,
 * not a gate. Being site-wide is why it belongs in the scale rather than outside it: the scale
 * is what makes "does the notice cover the mode control?" answerable, and nothing else was.
 */
export function CookieConsentBanner() {
  const { consent, accept, reject } = useCookieConsent();
  const noticeRef = useRef<HTMLElement | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);

  /*
   * Publishes the notice's height while it is up, and takes it back when it goes.
   *
   * A `ResizeObserver` and not a one-off measurement: the bar reflows on rotation, on a width
   * change, and whenever the text wraps differently — and a stale number would reserve the wrong
   * amount of room.
   *
   * `consent` **and** `isLeaving` are both in the dependencies because the component outlives the
   * bar: answering returns `null` from here, but this function stays mounted, so no cleanup fires
   * on its own. The re-run is the only thing that notices the element has gone — and it is the
   * second flag that marks that moment, since `consent` changes at the press, while the bar is
   * still descending and still reserving its room. With only `consent` in the list the variable
   * survived the bar and left a gap at the foot of every page afterwards.
   */
  useEffect(() => {
    const root = document.documentElement;
    const notice = noticeRef.current;

    if (notice === null) {
      root.style.removeProperty(COOKIE_NOTICE_HEIGHT_VARIABLE);
      return;
    }

    const publish = () => {
      const height = Math.ceil(notice.getBoundingClientRect().height);
      root.style.setProperty(COOKIE_NOTICE_HEIGHT_VARIABLE, `${height}px`);
    };

    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(notice);

    return () => {
      observer.disconnect();
      root.style.removeProperty(COOKIE_NOTICE_HEIGHT_VARIABLE);
    };
  }, [consent, isLeaving]);

  /*
   * Keeps the bar mounted for the length of its exit, and only then lets it go.
   *
   * The answer is written the instant it is given — a consent decision does not wait for an
   * animation — so `consent` has already left "pendente" by the time this runs, and it is this
   * flag alone that is still holding the element on the screen.
   */
  useEffect(() => {
    if (!isLeaving) return;

    const timer = setTimeout(() => setIsLeaving(false), COOKIE_NOTICE_EXIT_MS);
    return () => clearTimeout(timer);
  }, [isLeaving]);

  if (consent !== "pendente" && !isLeaving) return null;

  function decide(action: "accept" | "reject") {
    if (isLeaving) return;

    /*
     * Recorded first, animated second. If the person navigates mid-exit, or the tab is closed,
     * the answer is already in the cookie; what is lost is only the descent.
     */
    if (action === "accept") accept();
    else reject();

    // Before the event: a tag triggered by `cookie_consent` must already see the new state.
    sendConsentUpdate(action === "accept");
    track({ event: "cookie_consent", action });
    setIsLeaving(true);
  }

  return (
    <section
      ref={noticeRef}
      role="region"
      aria-label="Aviso de cookies"
      /*
       * Drives the exit in the stylesheet, and takes the answered bar out of the reading order
       * and out of reach of the pointer while it descends: it is no longer a question, and a
       * second press on a bar that is leaving must not land.
       */
      data-cookie-notice-leaving={isLeaving ? "" : undefined}
      aria-hidden={isLeaving ? true : undefined}
      inert={isLeaving ? true : undefined}
      /*
       * The layer comes from the one scale (`layer-stack.ts`), not from a number typed here.
       * It used to be a literal `z-[335]`, which put the notice over the floating mode control
       * (320) and the bulk bar (329) — both at this same bottom edge — so until it was answered
       * the person could not switch between "Minha lista", "Adicionar peças" and "Ver como
       * convidado": Playwright measured 54 refused clicks, this notice intercepting every one.
       * The notice is a yes/no, not a gate, so it now sits under every floating surface.
       */
      className={cn(
        "tb-cookie-banner fixed inset-x-0 bottom-0 border-t border-tb-border bg-tb-porcelain-tint text-tb-oxide",
        PANEL_LAYER_CLASS.cookieNotice,
      )}
    >
      {/*
        Tighter on a phone than it was, measured: 224px tall became 152px. The height is the whole
        problem on a small screen — at 224px the bar ate a quarter of a 852px-tall viewport — and
        the two words fit side by side at 320px with room to spare, so stacking them was costing
        48px and a gap for nothing.
      */}
      <div className="mx-auto flex max-w-[1200px] flex-col gap-3 px-[clamp(20px,4vw,56px)] py-3.5 md:flex-row md:items-center md:justify-between md:gap-8 md:py-5">
        <p className="text-[14px] leading-[1.6] md:max-w-[640px]">
          Usamos cookies para entender como o site é usado e melhorar a sua experiência.{" "}
          <Link href="/politica-de-privacidade" variant="inline" className="text-[14px]">
            Saiba mais na Política de Privacidade
          </Link>
          .
        </p>
        {/* `flex-1` on a phone so the two share the width; natural size from `md` up. */}
        <div className="flex flex-row gap-2.5 md:shrink-0">
          <Button
            type="button"
            variant="outline"
            className="min-h-[var(--tb-touch-min)] flex-1 md:flex-none"
            onClick={() => decide("reject")}
          >
            Recusar
          </Button>
          <Button
            type="button"
            variant="primary"
            className="min-h-[var(--tb-touch-min)] flex-1 md:flex-none"
            onClick={() => decide("accept")}
          >
            Aceitar
          </Button>
        </div>
      </div>
    </section>
  );
}
